import { isClosedStatus } from '@bbbk/schema';
import { DogCell, StatusPill, AreaPill, DataTable, DateCell, type Column } from '../common';
import { AREA_LABELS, VIEWING_STATUS_RECORDS } from './constants';
import {
  amountDue,
  nightsBetween,
  displayRatePerNight,
  formatOwnerLabel,
  nightsLabel,
  nightlyRateLabel,
} from './domain';
import { fmtDate, isoDate, dateOnly } from '../../lib/dates';
import { dayName } from '../staff/week';
import { fmtEuro } from '../../lib/money';
import { deriveBookingFlags } from './flags';
import { isSettled, viewingDogBreeds, viewingFlags, viewingSlotLabel } from '../today/viewings';
import type { ListState } from './filterTypes';
import type { Booking, ListRow, SortKey, SortState, Viewing } from './types';

export interface BookingsTableProps {
  // A booking or a viewing, discriminated (viewings-in-the-bookings-list). The
  // rows arrive in the order the server chose and are never re-sorted here.
  rows: ListRow[];
  // The Type in force — the header words follow it, and so does what loading
  // announces.
  kind?: ListState;
  // Sort is controlled by the parent and applied server-side (rows arrive
  // already ordered); the header only reflects state and reports clicks.
  sort?: SortState;
  onSort?: (k: SortKey) => void;
  // Open the row in the modal. THE WHOLE ROW, so a viewing can never be opened
  // as a booking (AC4). Optional for standalone tests.
  onRowClick?: (row: ListRow) => void;
  // While true, keep the header and show a column-matched skeleton body — the
  // header stays put on a sort/search re-fetch instead of the table reloading.
  loading?: boolean;
}

const DEFAULT_SORT: SortState = { key: 'added', dir: 'desc' };

// The cell a viewing cannot answer — Area, Due, the two dates, a dog nobody
// described. A dash rather than a blank: an empty cell reads as a value that
// failed to load, a dash as one that was never owed (the desk's rule).
const NONE = '\u2014';
const none = (cls: string) => (
  <div className={cls} role="cell">
    <span className="bt-none">{NONE}</span>
  </div>
);

const HEAD: Record<ListState, { dog: string; stay: string; loading: string }> = {
  booking: { dog: 'Dog(s)', stay: 'Stay', loading: 'Loading bookings…' },
  viewing: { dog: 'Contact', stay: 'Visit', loading: 'Loading viewings…' },
  all: { dog: 'Dog(s) / contact', stay: 'Stay / visit', loading: 'Loading bookings and viewings…' },
};

export function BookingsTable({
  rows,
  kind = 'booking',
  sort = DEFAULT_SORT,
  onSort = () => {},
  onRowClick,
  loading = false,
}: BookingsTableProps) {
  const today = isoDate(new Date());
  const head = HEAD[kind];

  // Each cell dispatches on the row's kind. The booking branches are the table
  // as it was; the viewing branches are the desk row's cells, in this table's
  // columns (StayTable's ViewingStayRow is the precedent).
  const columns: Column<ListRow, SortKey>[] = [
    {
      key: 'dog',
      label: head.dog,
      sortable: true,
      cell: (r) =>
        r.kind === 'booking' ? (
          <DogCell
            dogs={r.booking.dogs}
            ownerName={formatOwnerLabel(r.booking.ownerContacts)}
            flags={isClosedStatus(r.booking.status) ? [] : deriveBookingFlags(r.booking)}
          />
        ) : (
          // The contact leads, the telephone beneath, the Viewing word beside
          // the name — exactly as the desk row reads (AC3).
          <DogCell dogs={[]} person={{ name: r.viewing.contactName }} flags={viewingFlags()} subline={r.viewing.phone ?? ''} />
        ),
    },
    {
      key: 'breed',
      label: 'Breed',
      sortable: true,
      cell: (r) => {
        const text =
          r.kind === 'booking'
            ? [...new Set(r.booking.dogs.map((d) => d.breed).filter(Boolean))].join(' · ')
            : viewingDogBreeds(r.viewing.dogs);
        return text ? (
          <div className="bt-breed" role="cell">
            {text}
          </div>
        ) : (
          none('bt-breed')
        );
      },
    },
    {
      key: 'area',
      label: 'Area',
      sortable: true,
      cell: (r) =>
        r.kind === 'booking' ? (
          <div className="bt-area" role="cell">
            <AreaPill area={r.booking.area} label={AREA_LABELS[r.booking.area] ?? r.booking.area} />
          </div>
        ) : (
          none('bt-area')
        ),
    },
    {
      key: 'stay',
      label: head.stay,
      sortable: true,
      cell: (r) =>
        r.kind === 'booking' ? (
          <div className="bt-stay" role="cell">
            <div className="bt-stay-dates mono">
              {fmtDate(r.booking.checkIn)} → {fmtDate(r.booking.checkOut)}
            </div>
            <div className="bt-stay-nights">{nightsLabel(nightsBetween(r.booking.checkIn, r.booking.checkOut))}</div>
          </div>
        ) : (
          <div className="bt-stay" role="cell">
            <div className="bt-stay-dates mono">{`${dayName(dateOnly(r.viewing.viewingOn))} ${fmtDate(r.viewing.viewingOn)}`}</div>
            <div className="bt-stay-nights">{viewingSlotLabel(r.viewing.slot)}</div>
          </div>
        ),
    },
    {
      key: 'due',
      label: 'Due',
      sortable: true,
      cell: (r) => {
        if (r.kind !== 'booking') return none('bt-num total mono');
        const rate = displayRatePerNight(r.booking);
        return (
          <div className="bt-num total mono" role="cell">
            <div className="bt-due-amount amount">{fmtEuro(amountDue(r.booking))}</div>
            {rate != null && <div className="bt-due-rate">{nightlyRateLabel(rate)}</div>}
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      // Status is READ-ONLY in the table (no onClick) — it renders as a disabled
      // badge. Changing a status is done in Daily or the modal, never by a
      // stray click here (which previously cycled it and caused accidental
      // bulk flips). A viewing's pill carries its OWN vocabulary and tones,
      // never the booking ones; the Booked marker is keyed on the booking
      // link, never on the status (AC5).
      cell: (r) =>
        r.kind === 'booking' ? (
          <div role="cell">
            <StatusPill status={r.booking.status} />
          </div>
        ) : (
          <div role="cell" className="bt-status-viewing">
            <StatusPill record={VIEWING_STATUS_RECORDS[r.viewing.status]} />
            {r.viewing.bookingId && (
              <span className="desk-booked" title="This viewing produced a booking">
                Booked
              </span>
            )}
          </div>
        ),
    },
    {
      key: 'updated',
      label: 'Last updated',
      sortable: true,
      cell: (r) => (r.kind === 'booking' ? <DateCell value={r.booking.updatedAt} /> : none('bt-added mono')),
    },
    {
      key: 'added',
      label: 'Date added',
      sortable: true,
      cell: (r) => (r.kind === 'booking' ? <DateCell value={r.booking.createdAt} /> : none('bt-added mono')),
    },
  ];

  const bookingClass = (b: Booking) =>
    'status-' +
    b.status +
    (b.status === 'cancelled' ? ' cancelled' : '') +
    (isoDate(b.checkIn) === today || isoDate(b.checkOut) === today ? ' today-flag' : '');
  const viewingClass = (v: Viewing) => 'viewing' + (isSettled(v) ? ' done' : '');

  return (
    <DataTable
      className="bookings-table"
      columns={columns}
      rows={rows}
      rowKey={(r) => (r.kind === 'booking' ? r.booking.id : 'v' + r.viewing.id)}
      sort={sort}
      onSort={onSort}
      loading={loading}
      loadingLabel={head.loading}
      onRowClick={onRowClick}
      rowClassName={(r) => (r.kind === 'booking' ? bookingClass(r.booking) : viewingClass(r.viewing))}
    />
  );
}
